import { z } from 'zod';
import * as dt from '../data.types.js';
import { sanitizePemCertificateInput } from '../pem-utils.js';
import { isValidPhone, PHONE_VALIDATION_MESSAGE } from '../phone.utils.js';
/** PEM cert field after trimming whitespace and wrapping quotes. */
export const EkCertPemInputSchema = z
    .string()
    .transform(sanitizePemCertificateInput)
    .pipe(z.string().startsWith('-----BEGIN CERTIFICATE-----'));
export const AkCertPemInputSchema = z
    .string()
    .transform(sanitizePemCertificateInput)
    .pipe(z.string().min(1));
export const firstNameSchema = z.string().min(1).max(100).trim().brand('FirstName');
export const lastNameSchema = z.string().min(1).max(100).trim().brand('LastName');
export const companyNameSchema = z.string().min(1).max(100).trim().brand('CompanyName');
export const companyAliasSchema = z.string().min(1).max(100).trim().brand('CompanyAlias');
export const emailSchema = z.string().email().toLowerCase().brand('Email');
export const uuidSchema = z.string().brand('Uuid');
export const integerIdSchema = z.coerce.number().int().positive().brand('IntegerId');
export const nullableIntegerIdSchema = integerIdSchema.nullable();
const validPhoneStringSchema = z
    .string()
    .trim()
    .refine((val) => isValidPhone(val), { message: PHONE_VALIDATION_MESSAGE })
    .brand('PhoneNumber');
export const phoneNumberSchema = z
    .union([validPhoneStringSchema, z.null(), z.literal('')])
    .transform((val) => (val === '' ? null : val));
export const passwordSchema = z.string().min(1);
export const urlSchema = z.string().url().brand('Url');
export const dateSchema = z.coerce.date();
/**
 * Query-string booleans from Express (`"true"` / `"false"`).
 * Do not use `z.coerce.boolean()` for query params — it treats any non-empty string as true.
 */
export const optionalQueryBooleanSchema = z.preprocess((val) => {
    if (val === undefined || val === null || val === '') {
        return undefined;
    }
    if (val === true || val === 'true' || val === '1') {
        return true;
    }
    if (val === false || val === 'false' || val === '0') {
        return false;
    }
    return val;
}, z.boolean().optional());
export const waferFingerprintSchema = z.string().min(0).max(250).trim().brand('WaferFingerprint');
export const containerSlotSchema = z.coerce.number().int().min(1).max(25);
export const macAddressSchema = z.string().regex(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/);
export const ipAddressSchema = z.string().regex(/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/, 'Invalid IP address format');
export const newPasswordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');
export const containerTypeSchema = z.nativeEnum(dt.CONTAINER_TYPE);
export const containerSizeSchema = z.nativeEnum(dt.CONTAINER_SIZE);
export const containerStatusSchema = z.nativeEnum(dt.CONTAINER_STATUS);
export const waferStatusSchema = z.nativeEnum(dt.WAFER_STATUS);
export const waferPatternTypeSchema = z.nativeEnum(dt.WAFER_PATTERN_TYPE);
export const waferMaterialTypeSchema = z.nativeEnum(dt.WAFER_MATERIAL_TYPE);
export const waferThicknessTypeSchema = z.nativeEnum(dt.WAFER_THICKNESS_TYPE);
export const waferSizeSchema = z.nativeEnum(dt.WAFER_SIZE);
export const userTypeSchema = z.nativeEnum(dt.USER_TYPE);
export const bulkOperationTypeSchema = z.nativeEnum(dt.BULK_OPERATION_TYPE);
export const waferAuditStatusSchema = z.nativeEnum(dt.WAFER_AUDIT_STATUS);
export const baconStatusSchema = z.nativeEnum(dt.BACON_STATUS);
export const waferConflictTypeSchema = z.nativeEnum(dt.WAFER_CONFLICT_TYPE);
export const conflictStatusSchema = z.nativeEnum(dt.CONFLICT_STATUS);
export const conflictSeveritySchema = z.nativeEnum(dt.CONFLICT_SEVERITY);
export const resolutionActionSchema = z.nativeEnum(dt.RESOLUTION_ACTION);
export const BaseResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    data: z.string().optional(),
    error: z.string().optional(),
});
//# sourceMappingURL=primitives.js.map